import { useEffect, useState } from "react";
import type { ImageAsset, ImageType } from "../types/image-validation.types";
import { extractValidationWarnings } from "../utils/image-validation.utils";

/**
 * Hook to fetch image validation metadata for a title
 * Returns validation warnings extracted from ImageAsset metadata
 */
export function useImageValidation(titleId: string | null, mode: "submission" | "title") {
  const [validationWarnings, setValidationWarnings] = useState<Partial<Record<ImageType, string[]>>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only fetch for title mode
    if (mode !== "title" || !titleId) {
      setValidationWarnings({});
      return;
    }

    const fetchValidation = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("lumi-access-token");
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/admin/titles/${titleId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = (await response.json()) as { imageAssets?: ImageAsset[] };
          const warnings = extractValidationWarnings(data.imageAssets);
          setValidationWarnings(warnings);
        } else {
          console.error("Failed to fetch image validation:", response.status);
        }
      } catch (error) {
        console.error("Failed to fetch image validation:", error);
      } finally {
        setLoading(false);
      }
    };

    void fetchValidation();
  }, [mode, titleId]);

  return {
    validationWarnings,
    loading,
  };
}
